// AutomationRuleActionsSection.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import AutomationRuleActions from './AutomationRuleActions';
import Button from 'components/button/Button';
import ModalField from 'components/modal/field/ModalField';
import ModalLabel from 'components/modal/label/ModalLabel';

/**
 * AutomationRuleActionsSection
 *
 * Renders the actions header, rows, and add-action control for an automation rule.
 *
 * @param {object} rule - The automation rule being edited.
 * @param {array} actions - Current rule actions.
 * @param {array} fieldOptions - Available fields for action selection.
 * @param {object} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {object} fieldEnumOptionsByKey - Enum options lookup keyed by field key.
 * @param {string} baseKey - Active base key for member recipient selection.
 * @param {array} nodeTypeOptions - Base node type options with people fields.
 * @param {boolean} allowEmailActions - Whether send-email actions can be selected.
 * @param {function} updateRule - Updates a rule by key with an updater callback.
 * @param {function} handleRemoveAction - Removes an action row from a rule.
 * @param {function} handleAddAction - Adds an action row to a rule.
 *
 * @returns {ReactNode} The rendered automation rule actions section.
 */
const AutomationRuleActionsSection = ({
    rule,
    actions,
    fieldOptions,
    fieldTypeByKey,
    fieldEnumOptionsByKey,
    baseKey,
    nodeTypeOptions,
    allowEmailActions,
    updateRule,
    handleRemoveAction,
    handleAddAction,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // ----- RENDER ----- //

    return (
        <>
            <ModalField>
                <ModalLabel label="Then do..." />

                <AutomationRuleActions
                  rule={rule}
                  actions={actions}
                  theme={theme}
                  fieldOptions={fieldOptions}
                  fieldTypeByKey={fieldTypeByKey}
                  fieldEnumOptionsByKey={fieldEnumOptionsByKey}
                  baseKey={baseKey}
                  nodeTypeOptions={nodeTypeOptions}
                  allowEmailActions={allowEmailActions}
                  updateRule={updateRule}
                  handleRemoveAction={handleRemoveAction}
                />
            </ModalField>

            {/* ADD BUTTON */}
            <div className="automation-rule-add-button">
                <Button
                  label="+ Add Action"
                  fontSize="12px"
                  fontWeight="400"
                  foregroundColor={theme.foregroundMuted}
                  backgroundColor="transparent"
                  height="30px"
                  padding="0 10px"
                  onClick={() => handleAddAction(rule.key)}
                />
            </div>
        </>
    );
};

// Dev Tools
AutomationRuleActionsSection.displayName = 'AutomationRuleActionsSection';

export default AutomationRuleActionsSection;
